var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
/**
 * @desc dataset
 */
import * as lodash from '../utils/lodash';
import { cacheFunction } from '../utils/cache';
// eslint-disable-next-line no-unused-vars
export var defaultFilter = function (record, dataIndex) { return true; };
function canBeNumber(num) {
    return !isNaN(parseFloat(num));
}
/**
 * dataset
 */
var Dataset = /** @class */ (function () {
    function Dataset(datasource) {
        this._datasource = datasource;
        this.min = cacheFunction(this.min.bind(this));
        this.max = cacheFunction(this.max.bind(this));
        this.sum = cacheFunction(this.sum.bind(this));
        this._stackExtreme = cacheFunction(this._stackExtreme.bind(this));
    }
    Dataset.prototype.count = function (countFilter) {
        if (countFilter === void 0) { countFilter = defaultFilter; }
        return this._datasource.filter(countFilter).length;
    };
    Dataset.prototype.getData = function () {
        return this._datasource.slice();
    };
    Dataset.prototype.setData = function (data) {
        this._datasource = data;
    };
    Dataset.prototype.getRecord = function (dataIndex) {
        return this._datasource[dataIndex];
    };
    Dataset.prototype.getExtreme = function (dataKey) {
        return [this.min(dataKey), this.max(dataKey)];
    };
    Dataset.prototype.getStackedExtreme = function (datakey, stackedDataKey, filter) {
        if (filter === void 0) { filter = defaultFilter; }
        var params = __spreadArrays([datakey], stackedDataKey, [filter]);
        return this._stackExtreme.apply(null, params);
    };
    Dataset.prototype.mapData = function (callback) {
        return lodash.map(this._datasource, callback);
    };
    Dataset.prototype.eachData = function (callback) {
        return this._datasource.forEach(callback);
    };
    Dataset.prototype.getFieldData = function (dataKey, toNumber, filter, filterNaN) {
        var _this = this;
        if (toNumber === void 0) { toNumber = false; }
        if (filter === void 0) { filter = defaultFilter; }
        if (filterNaN === void 0) { filterNaN = false; }
        if (dataKey === '$index') {
            // dynamic field
            return this._datasource
                .map(function (record, index) { return index; })
                .filter(function (item, index) {
                return filter(_this._datasource[index], index);
            });
        }
        return lodash.filter(lodash.map(this._datasource, function (record) {
            return toNumber ? lodash.toNumber(record[dataKey]) : record[dataKey];
        }), function (value, index) {
            var record = _this._datasource[index];
            var val = lodash.get(record, dataKey);
            return filter(_this._datasource[index], index) && !(filterNaN && !canBeNumber(val));
        });
    };
    Dataset.prototype.avg = function (dataKey) {
        var sum = this.sum(dataKey);
        return sum / this._datasource.length;
    };
    Dataset.prototype.min = function (dataKey, filter) {
        if (filter === void 0) { filter = defaultFilter; }
        return lodash.min(this.getFieldData(dataKey, true, filter, true));
    };
    Dataset.prototype.max = function (dataKey, filter) {
        if (filter === void 0) { filter = defaultFilter; }
        return lodash.max(this.getFieldData(dataKey, true, filter, true));
    };
    Dataset.prototype.sum = function (dataKey, filter) {
        if (filter === void 0) { filter = defaultFilter; }
        return lodash.sum(this.getFieldData(dataKey, true, filter, false));
    };
    Dataset.prototype.uniq = function (dataKey) {
        return lodash.uniq(this.getFieldData(dataKey));
    };
    Dataset.prototype.findIndex = function (callback) {
        return lodash.findIndex(this._datasource, callback);
    };
    Dataset.prototype.stack = function (datakey, stackedDataKey, filter, keepNull, fillOrigin) {
        if (filter === void 0) { filter = defaultFilter; }
        if (keepNull === void 0) { keepNull = false; }
        if (fillOrigin === void 0) { fillOrigin = 0; }
        var areaFillOrigin = 0;
        return this._datasource.filter(filter).map(function (record) {
            var stackValues = stackedDataKey.map(function (dataKey) { return record[dataKey]; });
            var current = record[datakey];
            var sum = null;
            stackValues.forEach(function (value) {
                if ((canBeNumber(value) && value * current > 0) || (current === 0 && value > 0)) {
                    sum += parseFloat(value);
                }
            });
            if (!keepNull && sum === null) {
                sum = 0;
                areaFillOrigin = fillOrigin;
            }
            if (canBeNumber(current)) {
                return [sum + areaFillOrigin, sum + parseFloat(current)];
            }
            return [sum, sum];
        });
    };
    Dataset.prototype.sumFields = function (fields) {
        return this._datasource.map(function (record) {
            return lodash.sum(fields.map(function (field) {
                var value = lodash.toNumber(lodash.get(record, field));
                return isNaN(value) ? 0 : value;
            }));
        });
    };
    Dataset.prototype.intervalStack = function (datakey, filter) {
        var _this = this;
        if (filter === void 0) { filter = defaultFilter; }
        var values = [];
        this.getFieldData(datakey, true).reduce(function (prev, current, index) {
            var record = _this._datasource[index];
            var next = !isNaN(current) ? (filter(record, index) ? prev + current : prev + 0) : prev;
            values.push([prev, next]);
            return next;
        }, 0);
        return values;
    };
    Dataset.prototype.setSortData = function (dataKey, order) {
        var sortData = lodash.orderBy(this._datasource, [dataKey], [order]);
        this._datasource = sortData;
    };
    Dataset.prototype.sortData = function (dataKey, order) {
        return lodash.orderBy(this._datasource, [dataKey], [order]);
    };
    Dataset.prototype.intervalAvg = function (datakeyList) {
        if (datakeyList.length === 0) {
            return [];
        }
        return this.mapData(function (record) {
            return lodash.sum(datakeyList.map(function (dataKey) { return record[dataKey]; })) / datakeyList.length;
        });
    };
    Dataset.prototype.pickFieldValues = function (fields) {
        return this.mapData(function (record) { return fields.map(function (field) { return record[field]; }); });
    };
    /**
     * @param dataKey field
     * @param denominator percent of what
     */
    // eslint-disable-next-line no-unused-vars
    Dataset.prototype.percent = function (dataKey, denominator) {
        // @todo percent
        return [];
    };
    Dataset.prototype._stackExtreme = function () {
        var input = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            input[_i] = arguments[_i];
        }
        var dataKey = input[0];
        var stackedDataKey = input.slice(1, -1);
        var filter = lodash.last(input);
        var stackData = this.stack(dataKey, stackedDataKey, filter, true);
        var allData = lodash.flatten(stackData).filter(function (val) { return val !== null; });
        return [lodash.min(allData), lodash.max(allData)];
    };
    return Dataset;
}());
export default Dataset;
